import React, { useState } from 'react';
import {
    Box,
    ColumnLayout,
    Container,
    ExpandableSection,
    Grid,
    Header,
    Icon,
    Pagination,
    Popover,
    Table,
} from '@amzn/awsui-components-react';
import TPPieChart from '../../shared/TPPieChart';
import { MARReportSummary } from 'src/models/reports/ReportGenerationSummary';
import { getReasonCodeDescription } from './util';

type ComponentProps = {
    summary?: MARReportSummary;
}

export default function MARReportSummarySection({ summary }: ComponentProps) {
    const PAGE_SIZE = 20;
    const [currentPage, setCurrentPage] = useState(1);

    if (!summary) {
        return (
            <Grid gridDefinition={[{ colspan: { default: 7, xl: 8 } }, { colspan: { default: 5, xl: 4 } }]}>
                <Container header={<Header variant="h2" description="CLI-level report generation status for Monthly Accounting Reports">MAR Report Status</Header>}>
                    <Box textAlign="center" color="inherit"><b>No data available</b><Box variant="p" color="inherit">No MAR report status data available to display</Box></Box>
                </Container>
                <Box textAlign="center" color="inherit"><b>No data available</b><Box variant="p" color="inherit">No MAR data available to display on the chart</Box></Box>
            </Grid>
        );
    }

    const calcPercentage = (count: number) =>
        summary.totalExpected ? (count / summary.totalExpected) * 100 : 0;

    const pieChartData = [
        { title: 'Success', percentage: calcPercentage(summary.successCount), value: summary.successCount },
        { title: 'Failed', percentage: calcPercentage(summary.failureCount), value: summary.failureCount },
        { title: 'Pending', percentage: calcPercentage(summary.pendingCount), value: summary.pendingCount },
    ].filter(slice => slice.value > 0);

    const reasonCodeEntries = Object.entries(summary.reasonCodeSummary);

    return (
        <Grid gridDefinition={[{ colspan: { default: 7, xl: 8 } }, { colspan: { default: 5, xl: 4 } }]}>
            <Container header={
                <Header variant="h2"
                    description="CLI-level report generation status for Monthly Accounting Reports">
                    MAR Report Status
                </Header>}>
                <ColumnLayout columns={5} variant="text-grid">
                    <div>
                        <Box variant="awsui-key-label">Success</Box>
                        <Box variant="h1">{summary.successCount}</Box>
                    </div>
                    <div>
                        <Box variant="awsui-key-label">Failed</Box>
                        <Box variant="h1">{summary.failureCount}</Box>
                    </div>
                    <div>
                        <Box variant="awsui-key-label">Pending</Box>
                        <Box variant="h1">{summary.pendingCount}</Box>
                    </div>
                    <div>
                        <Box variant="awsui-key-label">Total CLIs</Box>
                        <Box variant="h1">{summary.totalExpected}</Box>
                    </div>
                    <div>
                        <Box variant="awsui-key-label">Success Rate</Box>
                        <Box variant="h1">{calcPercentage(summary.successCount).toFixed(1)}%</Box>
                    </div>
                </ColumnLayout>

                {reasonCodeEntries.length > 0 && (
                    <ExpandableSection headerText="Failure Breakdown by Reason Code" variant="footer">
                        <Table
                            columnDefinitions={[
                                { id: 'reasonCode', header: 'Reason Code', cell: (item: [string, number]) => (
                                    <Popover header={item[0]} content={getReasonCodeDescription(item[0])}>
                                        <Box color="text-status-info" fontSize="body-s">{item[0]} <Icon name="status-info" size="small" /></Box>
                                    </Popover>
                                )},
                                { id: 'count', header: 'Count', cell: (item: [string, number]) => item[1] },
                            ]}
                            items={reasonCodeEntries}
                            variant="embedded"
                        />
                    </ExpandableSection>
                )}

                {summary.failedCLIs.length > 0 && (
                    <ExpandableSection headerText={`Failed CLIs (${summary.failedCLIs.length})`} variant="footer">
                        <Table
                            columnDefinitions={[
                                { id: 'cli', header: 'CLI', cell: (item) => item.cli },
                                { id: 'reasonCode', header: 'Reason Code', cell: (item) => (
                                    <Popover header={item.reasonCode} content={getReasonCodeDescription(item.reasonCode)}>
                                        <Box color="text-status-info" fontSize="body-s">{item.reasonCode} <Icon name="status-info" size="small" /></Box>
                                    </Popover>
                                )},
                                { id: 'tpTaxOwner', header: 'TP Tax Owner', cell: (item) => item.tpTaxOwner || '—' },
                            ]}
                            items={summary.failedCLIs.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)}
                            variant="embedded"
                            pagination={
                                summary.failedCLIs.length > PAGE_SIZE ? (
                                    <Pagination
                                        currentPageIndex={currentPage}
                                        pagesCount={Math.ceil(summary.failedCLIs.length / PAGE_SIZE)}
                                        onChange={({ detail }) => setCurrentPage(detail.currentPageIndex)}
                                    />
                                ) : undefined
                            }
                        />
                    </ExpandableSection>
                )}
            </Container>
            <TPPieChart
                data={pieChartData}
                totalUnits={summary.totalExpected}
                ariaDescription="Donut chart showing MAR report generation status"
                ariaLabel="MAR report status donut chart"
                emptyDataDescription="No MAR report status data available"
                noMatchDataDescription="No MAR data for the selected filter criteria"
            />
        </Grid>
    );
}
